import {
  AlertTriangle,
  Bookmark,
  BookmarkCheck,
  ExternalLink,
  FileText,
  MessageSquarePlus,
} from "lucide-react";
import type { KeyboardEvent } from "react";
import type { SourceDocument } from "../lib/documentSearch/types";

type SourceCardProps = {
  document: SourceDocument;
  rank: number;
  selected: boolean;
  pinned: boolean;
  chatSelected: boolean;
  chatSelectionFull: boolean;
  onSelect: () => void;
  onPinToggle: () => void;
  onChatToggle: () => void;
};

export function SourceCard({
  document,
  rank,
  selected,
  pinned,
  chatSelected,
  chatSelectionFull,
  onSelect,
  onPinToggle,
  onChatToggle,
}: SourceCardProps) {
  const confidence = document.confidence ?? document.relevance / 100;
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <article className={`source-card ${selected ? "is-selected" : ""}`}>
      <div
        className="source-card__main"
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={handleKeyDown}
      >
        <div className="source-card__topline">
          <span className="rank-badge">#{rank}</span>
          <FileTypeIcon fileName={document.fileName} />
          <span className="document-type">
            <FileText size={15} aria-hidden="true" />
            {document.type}
          </span>
          <span className="match-badge">{document.matchStrength}</span>
          <span className="confidence-badge">
            Confidence <strong>{confidence.toFixed(3)}</strong>
          </span>
          {document.chatSummary?.isLarge ? (
            <span
              className="large-source-badge"
              title="This source exceeds the large-document size threshold"
            >
              <AlertTriangle size={13} aria-hidden="true" />
              Large source
            </span>
          ) : null}
        </div>
        <h3>{document.title}</h3>
        <p className="source-card__snippet">{document.excerpt}</p>
        <div className="source-card__meta">
          <span>{document.collectionName}</span>
          <span>{document.year > 0 ? document.year : "Year not set"}</span>
          <span>{document.pages > 0 ? `${document.pages} pages` : "Source file"}</span>
        </div>
        <div className="relevance-meter" aria-label={`${document.relevance}% relevance`}>
          <span style={{ width: `${document.relevance}%` }} />
        </div>
      </div>
      <div className="source-card__actions">
        <button
          className={chatSelected ? "tool-button is-active" : "tool-button"}
          type="button"
          disabled={!document.chatSummary?.available || (chatSelectionFull && !chatSelected)}
          title={
            !document.chatSummary?.available
              ? "Generate the rich Markdown summary before chatting with this source"
              : chatSelectionFull && !chatSelected
                ? "A document chat can include up to 6 sources"
                : chatSelected
                  ? "Remove from document chat"
                  : "Add to document chat"
          }
          onClick={onChatToggle}
        >
          <MessageSquarePlus size={17} aria-hidden="true" />
          <span>{chatSelected ? "Selected" : "Chat"}</span>
        </button>
        <button className="tool-button" type="button" onClick={onPinToggle}>
          {pinned ? (
            <BookmarkCheck size={17} aria-hidden="true" />
          ) : (
            <Bookmark size={17} aria-hidden="true" />
          )}
          <span>{pinned ? "Pinned" : "Pin"}</span>
        </button>
        <a
          className="tool-button tool-button--link"
          href={document.sourceUrl}
          target="_blank"
          rel="noreferrer"
        >
          <ExternalLink size={17} aria-hidden="true" />
          <span>Open</span>
        </a>
      </div>
    </article>
  );
}

function FileTypeIcon({ fileName }: { fileName: string }) {
  const extension = fileName.split(".").at(-1)?.toLowerCase() ?? "";
  const knownTypes: Record<string, { label: string; className: string }> = {
    pdf: { label: "PDF", className: "pdf" },
    doc: { label: "DOC", className: "doc" },
    docx: { label: "DOCX", className: "doc" },
    xls: { label: "XLS", className: "sheet" },
    xlsx: { label: "XLSX", className: "sheet" },
    ppt: { label: "PPT", className: "slides" },
    pptx: { label: "PPTX", className: "slides" },
    md: { label: "MD", className: "text" },
    txt: { label: "TXT", className: "text" },
    zip: { label: "ZIP", className: "archive" },
  };
  const type = knownTypes[extension] ?? { label: "FILE", className: "file" };

  return (
    <span
      aria-label={`${type.label} file`}
      className={`file-type-icon file-type-icon--${type.className}`}
      title={`${type.label} file`}
    >
      <FileText size={14} aria-hidden="true" />
      <span>{type.label}</span>
    </span>
  );
}
